import { Component } from "react"
import PropTypes from "prop-types"
import { omit, find, isEmpty } from "lodash"
import { Paper, FormControl, TextField, InputAdornment, Popover, InputLabel } from "@mui/material"
import { Search, InfoOutlined } from "@mui/icons-material"
import ModelSelector from "modules/model-selector"
import OPTooltip from "components/material/tooltip"
import { dollarFormatter, NEGATIVE_FORMATS } from "helpers/formatters/numbers"
import BasketTypes from "./basket-types"

import "./style.scss"

export default class SmartTradeSetup extends Component {
  static propTypes = {
    instrumentDescriptors: PropTypes.object.isRequired,
    tradeSize: PropTypes.number,
    basketSize: PropTypes.number,
    basketType: PropTypes.oneOf(["long", "short"]),
    tradeDate: PropTypes.string,
    Date: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    instrumentLatestDate: PropTypes.string.isRequired,
    instrumentEarliestDate: PropTypes.string.isRequired,
    referenceInstrument: PropTypes.object.isRequired,
    setModel: PropTypes.func.isRequired,
    modelId: PropTypes.string.isRequired,
    models: PropTypes.array.isRequired,
    instrumentPortfolio: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    instrumentPortfolioGMV: PropTypes.number,
    completedStep: PropTypes.number,
    longOrShortInstrument: PropTypes.oneOf(["LONG_ONLY", "SHORT_ONLY", "LONG_SHORT"]),
  }

  constructor(props) {
    super(props)

    this.state = {
      infoAnchor: null,
      tradeSize: props.tradeSize || "",
      basketSize: props.basketSize || "",
      basketType: props.basketType || "short",
      errors: {},
    }
  }

  componentDidMount() {
    const { basketSize } = this.state
    const { completedStep } = this.props

    // this is to prevent the shared(security & portfolio) default value of -100 from
    // being applied by default.
    if (basketSize && !completedStep) {
      this.setState({ basketSize: "" })
    }
  }

  openInfo = ev => {
    this.setState({ infoAnchor: ev.currentTarget })
  }

  closeInfo = () => {
    this.setState({ infoAnchor: null })
  }

  onChange = (event) => {
    const { value } = event.target
    const name = event.target.name
    const changes = {
      errors: { ...this.state.errors },
      [name]: value,
    }

    let hasError = false
    if (!Number.isFinite(parseFloat(value)) && !!value) {
      hasError = true
    }

    if (hasError && !changes.errors[name]) {
      changes.errors[name] = true
    } else if (!hasError && changes.errors[name]) {
      changes.errors = omit(changes.errors, name)
    }

    const newState = { ...this.state, ...changes }
    this.setState(newState)

    let propsChanges = { [name]: value ? parseFloat(value) : value }
    if (hasError) {
      propsChanges = { [name]: null }
    }
    this.props.onChange(propsChanges)
  }

  onBasketChange = event => {
    const { instrumentPortfolioGMV } = this.props
    const { value } = event.target
    const name = event.target.name

    // Ui changes
    const changes = {
      errors: { ...this.state.errors },
      [name]: value,
    }

    let hasError = false
    // BasketType always has a value
    const floatValue = parseFloat(value)
    if (name !== "basketType" && !!value && (!Number.isFinite(floatValue) || floatValue <= 0)) {
      hasError = true
    }

    if (hasError && !changes.errors[name]) {
      changes.errors[name] = true
    } else if (!hasError && changes.errors[name]) {
      changes.errors = omit(changes.errors, name)
    }

    const newState = { ...this.state, ...changes }
    this.setState(newState, () => {
      // AFTER the value has been changed, we can calculate the tradeSize for display
      const { basketSize, basketType } = this.state

      // Actual smart trade setup changes
      let propChanges = {
        ...changes,
        portfolio: true,
        tradeSize: instrumentPortfolioGMV,
        basketRules: {
          basketSize: parseFloat(basketSize) * (basketType === "short" ? -1 : 1),
        },
      }
      if (hasError) {
        propChanges = {
          ...changes,
          [name]: null,
        }
      }
      this.props.onChange(propChanges)
    })
  }

  getRefInstrumentName = () => {
    const { referenceInstrument, instrumentDescriptors, instrumentPortfolio } = this.props
    const { securityId } = referenceInstrument
    if (securityId) {
      const { ticker, mic } = securityId
      let name = `${ticker}.${mic}`
      if (!isEmpty(instrumentDescriptors)) {
        name = `${ticker} - ${instrumentDescriptors.name}`
      }
      return name
    }
    if (instrumentPortfolio) {
      return instrumentPortfolio.name
    }
    return ""
  }

  basketSizeHelperText = () => {
    const { instrumentPortfolioGMV } = this.props
    const { basketSize, basketType, errors } = this.state

    if (errors.basketSize) { return "Invalid basket size." }
    const tradeSize = instrumentPortfolioGMV * (parseFloat(basketSize) / 100) * (basketType === "short" ? -1 : 1)

    return `Portfolio: ${dollarFormatter(instrumentPortfolioGMV, NEGATIVE_FORMATS.PARENTHESES)}, Basket: ${!basketSize ? "-" : dollarFormatter(tradeSize, NEGATIVE_FORMATS.PARENTHESES)}`
  }

  render() {
    const { instrumentEarliestDate, instrumentLatestDate, referenceInstrument, models, modelId, setModel, instrumentPortfolio, tradeDate, longOrShortInstrument } = this.props
    const { tradeSize, basketSize, basketType, errors, infoAnchor } = this.state
    const selectedModel = find(models, model => model.id === modelId)
    const resourceId = referenceInstrument.portfolioId || referenceInstrument.securityId

    const tradeSizeLabel = <div className="smart-trade-input-label-wrapper">
      <span>Trade Size, USD (in millions)</span>
      <InfoOutlined className="smart-trade-input-info-icon" onMouseEnter={this.openInfo} onMouseLeave={this.closeInfo} />
      <Popover
        className="smart-trade-info-wrapper"
        open={!!infoAnchor}
        anchorEl={infoAnchor}
        style={{ pointerEvents: "none" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={this.closeInfo}
        disableRestoreFocus
      >
        <div className="smart-trade-input-info">
          <span>The default basket size is calculated as a -100% from the trade size — the negative sign denotes that the basket is on the opposite side of the book.</span>
          <span>The basket size can be changed in Step 3: Basket Rules</span>
        </div>
      </Popover>
    </div>

    return <Paper className="components_smart-trade-setup" square elevation={1}>
      <div className="smart-trade-setup-wrapper">
        <div className="smart-trade-setup-goal-wrapper">
          <span className="title">Basket Goal</span>
          <span className="goal">Focus on alpha by minimizing unwanted factor risk</span>
        </div>
        <div className="smart-trade-input-wrapper">
          <div className="smart-trade-input-row-1">
            <FormControl
              variant="standard"
              className="smart-trade-setup-instrument smart-trade-setup-input"
            >
              <TextField
                variant="standard"
                disabled
                name="smart-trade-instrument"
                label="Reference Instrument"
                value={this.getRefInstrumentName()}
                margin="normal"
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start"><Search /></InputAdornment>,
                  },
                }}
              />
            </FormControl>
            <FormControl
              variant="standard"
              className="smart-trade-setup-model-selector smart-trade-setup-input"
            >
              {/* This InputLabel is a sibling of ModelSelector, not nested inside its TextField, so
                  it reads this FormControl's own context -- which never sees ModelSelector's
                  internal input as "filled" (that state lives on the FormControl nested inside
                  ModelSelector's own TextField, a separate context). Auto shrink-detection can
                  never bridge that boundary, so force it explicitly; modelId is a required prop,
                  always truthy once this renders. */}
              <InputLabel htmlFor="modelId" shrink={Boolean(modelId)}>Risk Model</InputLabel>
              <ModelSelector
                noSpinner
                modelId={modelId}
                modelName={selectedModel ? selectedModel.name : modelId}
                setModel={setModel}
                resourceType={referenceInstrument.type}
                resourceId={resourceId}
                assetAvailability={{
                  earliest: instrumentEarliestDate,
                  latest: instrumentLatestDate,
                }}
              />
            </FormControl>
          </div>
          <div className="smart-trade-input-row-2">
            <FormControl
              variant="standard"
              className="smart-trade-setup-trade-date smart-trade-setup-input"
            >
              <OPTooltip classes={{ popper: "smart-trade-date" }} title="This date cannot be changed. Create a new Research Topic to use a different trade date.">
                <TextField
                  variant="standard"
                  disabled
                  label="Trade Date"
                  name="smart-trade-tradeDate"
                  value={tradeDate}
                />
              </OPTooltip>
            </FormControl>

            {!instrumentPortfolio
              && <FormControl
                variant="standard"
                className="smart-trade-setup-input-size smart-trade-setup-input"
              >
                <TextField
                  variant="standard"
                  name="tradeSize"
                  label={tradeSizeLabel}
                  value={tradeSize}
                  margin="normal"
                  onChange={this.onChange}
                  error={errors.tradeSize}
                  helperText={errors.tradeSize ? "Invalid trade size." : ""}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start">$</InputAdornment>,
                    },

                    formHelperText: { error: errors.tradeSize },
                  }}
                />
              </FormControl>}
            {instrumentPortfolio && <div className="smart-trade-setup-grouped-input">
              <FormControl variant="standard" className="smart-trade-setup-input-basketSize">
                <TextField
                  variant="standard"
                  name="basketSize"
                  label="Basket size, % equity"
                  value={basketSize}
                  margin="normal"
                  onChange={this.onBasketChange}
                  error={errors.basketSize}
                  helperText={this.basketSizeHelperText()}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start">%</InputAdornment>,
                    },

                    formHelperText: { error: errors.basketSize },
                  }}
                />
              </FormControl>
              <FormControl variant="standard" className="smart-trade-setup-input-basketType">
                <InputLabel id="basketType">Basket type</InputLabel>
                <BasketTypes
                  labelId="basketType"
                  name="basketType"
                  value={basketType}
                  label="Basket Type"
                  onChange={this.onBasketChange}
                  longOrShortInstrument={longOrShortInstrument}
                />
              </FormControl>
            </div>}
          </div>
        </div>
      </div>
    </Paper>
  }
}
